import React, { useEffect, useState } from 'react';

import CloseIcon from '@/assets/icons/close.svg';

import {
  dateRangeMedWithZoneOffset,
  isDateTimeInRange,
} from '../../../helpers/dates';
import { isBrowser } from '../../../helpers/is-browser';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import { IContentfulImage } from '../../../interfaces/contentful-image.interface';

import { BlockWrapper } from '../../block-wrapper/block-wrapper';
import { BlockSections } from '../../blocks/block-sections';
import { RenderBlocks } from '../../blocks/render-blocks';
import ImageWrapper from '../../image-wrapper/Image-wrapper';
import { PageComponentMainBottomBorder } from './page-component-main-bottom-border';
import { PageComponentMainHeader } from './page-component-main-header';
import { ILibraryOpeningHours } from '@/lib/fragments/libraryOpeningHours';
import { ILibraryOpeningHoursException } from '@/lib/fragments/libraryOpeningHoursException';
import { ILibraryMeetingPlaceMinified } from '@/lib/fragments/libraryMeetingPlace';
import { useUser } from '@/providers/UserProvider';
import {
  clearUrlParams,
  isBookingSubdomain,
  resolveSitePathPrefix,
  setLocalStorageItem,
} from '@/helpers/utils';
import Button from '@/components/ui/button/button';
import { RichText } from '@/components/rich-text/rich-text';
import { Share } from '@/components/share/share';
import { FavouriteButton } from '@/components/ui/favourite-button/favourite-button';
import { FavouriteType } from '@/context/FavouritesContext';
import Backdrop from '@/components/ui/backdrop/backdrop';
import { SideModal } from '@/components/ui/side-modal/side-modal';
import { SideModalBody } from '@/components/ui/side-modal/side-modal-body';
import { ScanQRCode } from '../special-offer-redemption/scan-qr-code';
import { getCompleteData } from '../special-offer-redemption/offer-redemption-api-helper';
import { SpecialOfferRedemtion } from '../special-offer-redemption/special-offer-redemption';
import { LivliFamilyOfferRedemption } from '../special-offer-redemption/livli-family-offer-redemption';
import {
  useGetLibraryConstantsDictionaryData,
  useGetTimezone,
  useTenantConfig,
} from '@/providers/ConfigProvider';
import SlotCard from '@/components/cards/card-slots';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import TenantDescription from '../tenant-detail-page/tenant-description';
import ShowMore from '../list-page/show-more';
import { getEventSlotsData } from '@/helpers/api/livli-family';
import FlameIcon from '@/assets/icons/Flame.svg';
import { CountdownState } from '@/helpers/countdown';
import Flame from '@/assets/icons/Flame.svg';
import { getTagBorderColor } from '@/helpers/tailwindColors';
import { getNextSlotText } from '@/helpers/list-page/list-page-helpers';
import { useProfileSlider } from '@/providers/ProfileSliderProvider';
import { redirect } from 'next/navigation';

interface MainBlock {
  pageTitle?: string;
  header: string;
  caption?: string;
  typeTag?: string;
  typeTagClass?: string;
  panelclasses?: string;
  validFrom?: string;
  validTo?: string;
  node_locale: string;
  showSocialShare: boolean;
  logo?: IContentfulImage;
  meetingPlaceOpeningHours?: {
    openingHours?: ILibraryOpeningHours;
    openingHoursExceptions?: ILibraryOpeningHoursException[];
    timezone?: string;
  };
  tags?: string[];
  tagItems?: { id: string; tagName: string; colour: string }[];
  offerRedemptionMechanism?: string;
  offerRedemptionDetails?: {
    internalName: string;
    richText: any;
  };
  typeOfOffer?: string;
  contentful_id?: string;
  tenantName?: string;
  meetingPlaceHomePage?: ILibraryMeetingPlaceMinified;
  showSpecialOfferRedemption?: boolean;
  isLivliFamily?: boolean;
  isLivliFamilyOffer?: boolean;
  beResourceId?: string;
  offerTenantId?: string | string[];
  favouriteId?: string;
  favouriteType?: FavouriteType;
  countdownState?: CountdownState;
  pathPrefix?: string;
  disclaimerText?: string | null;
}
interface MainBlockRichText extends MainBlock {
  blocks?: BlockSections[];
  richText: any;
}
interface MainBlockBlocks extends MainBlock {
  richText?: any;
  blocks: BlockSections[];
}

export const PageComponentMain = ({
  pageTitle,
  header,
  caption,
  typeTag,
  typeTagClass,
  panelclasses,
  richText,
  blocks,
  validFrom,
  validTo,
  node_locale,
  showSocialShare,
  logo,
  meetingPlaceOpeningHours,
  tags,
  tagItems,
  offerRedemptionDetails,
  offerRedemptionMechanism = '',
  typeOfOffer,
  contentful_id,
  tenantName,
  meetingPlaceHomePage,
  showSpecialOfferRedemption = false,
  favouriteId,
  favouriteType,
  countdownState = { type: 'none' },
  isLivliFamily = false,
  pathPrefix,
  isLivliFamilyOffer = false,
  beResourceId,
  offerTenantId,
  disclaimerText,
}: MainBlockRichText | MainBlockBlocks) => {
  const [openRightDrawer, setOpenRightDrawer] = useState<boolean>(false);
  const [isOfferRedemptionMobileViewOpen, setIsOfferRedemptionMobileViewOpen] =
    useState<boolean>(false);
  const [showFullScreenQR, setShowFullScreenQR] = useState(false);
  const [isQRCodeScanned, setIsQRCodeScanned] = useState(false);
  const [encryptedScannedText, setEncryptedScannedText] = useState('');
  const [offerBookingData, setOfferBookingData] = useState([] as any);
  const [eventSlotsData, setEventSlotsData] = useState([]);
  const [showClaimLoader, setShowClaimLoader] = useState(false);
  const [showSlotsLoader, setShowSlotsLoader] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const { user } = useUser();
  const isMobile = useIsMobile();
  const timezone = useGetTimezone();
  const config = useTenantConfig();

  const prefix = resolveSitePathPrefix(config?.GATSBY_SITE_PATH_PREFIX);

  const bookingEngineLink = richText?.links?.entries?.block.find((it: any) =>
    isBookingSubdomain(it?.externalLink),
  );

  const { openSlider } = useProfileSlider();

  const dictionary = useGetLibraryConstantsDictionaryData();

  const _subSlotsSize = 3;

  const [slotsPage, setSlotspage] = useState(1);

  const aspectRatio =
    logo?.width && logo?.height
      ? parseInt(logo.width) / parseInt(logo.height)
      : undefined;

  const isNearlySquare =
    typeof aspectRatio === 'number' && Math.abs(aspectRatio - 1) < 0.1;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    isOfferRedemptionMobileViewOpen
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [isOfferRedemptionMobileViewOpen]);

  useEffect(() => {
    const getMPData = async () => {
      try {
        setShowClaimLoader(true);
        const offerData = await getCompleteData(
          {
            id: meetingPlaceHomePage?.businessUnitCode,
          },
          {
            'X-Current-Language': node_locale,
            'X-Preferred-Language': node_locale,
          },
        );
        if (offerData) {
          setOfferBookingData(
            offerData?.offer?.filter((item: any) => item?.id == contentful_id),
          );
        }
        setShowClaimLoader(false);
      } catch (err: any) {
        console.error('Failed to fetch user:', err);
      } finally {
        setShowClaimLoader(false);
      }
    };
    if (showSpecialOfferRedemption && !isLivliFamilyOffer) {
      getMPData();
    }
  }, []);

  useEffect(() => {
    setShowSlotsLoader(true);
    const getEventSlots = async () => {
      try {
        const slotsData = await getEventSlotsData({
          mpId: meetingPlaceHomePage?.businessUnitCode,
          cmsId: contentful_id,
        });
        setEventSlotsData(slotsData[contentful_id || '']);
      } catch (err: any) {
        console.error('Failed to fetch user:', err);
      } finally {
        setShowSlotsLoader(false);
      }
    };
    if (isLivliFamily) {
      getEventSlots();
    }
  }, []);

  useEffect(() => {
    if (isBrowser) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('modal') === 'claim-offer') {
        if (user && offerRedemptionMechanism == 'Dynamic') {
          setShowFullScreenQR(true);
        } else if (user && offerRedemptionMechanism == 'Static') {
          if (isMobile) {
            setIsOfferRedemptionMobileViewOpen(true);
          } else {
            setOpenRightDrawer(true);
          }
        }
      }
    }
  }, [user, isMobile]);

  const activeEventSlotsData = eventSlotsData?.filter((event: any) => {
    const eventDate = new Date(event.date + 'T00:00:00');
    return eventDate >= today;
  });

  // Livli Family: an offer tagged "Car Parking" routes the primary CTA to the
  // parking profile page instead of the normal claim/redemption flow — mirrors
  // the Car Parking behaviour on the service detail page.
  const CAR_PARKING_TAG = 'Car Parking';
  const isCarParkingOffer =
    isLivliFamily &&
    favouriteType === 'offer' &&
    (tagItems?.some((tag) => tag?.tagName === CAR_PARKING_TAG) ?? false);

  const goToParkingProfile = () => {
    if (!isBrowser) return;
    const origin = window?.location?.origin ?? '';
    const prefix =
      process.env.NEXT_PUBLIC_ENV == 'development' ? '' : pathPrefix;
    const mpid = meetingPlaceHomePage?.businessUnitCode;
    const path = [prefix, node_locale, 'profile', 'parking']
      .filter(Boolean)
      .join('/');
    // Full-page navigation so middleware runs and sets the `lw-mp-id` cookie
    // for the profile route (same pattern as the tab-bar link).
    if (user) {
      window.location.href = `${origin}/${path}?mpid=${mpid}`;
    } else {
      openSlider(`/${path}?mpid=${mpid}`);
    }
  };

  const claimOffer = () => {
    // console.log(offerBookingData && offerBookingData[0].bookingDetails.id, 'offerBookingData');
    if (isCarParkingOffer) {
      goToParkingProfile();
      return;
    }
    if (isBrowser) {
      const params = new URLSearchParams(window.location.search);
      params.set('modal', 'claim-offer');
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      setLocalStorageItem('current_path', newUrl);
      window.history.pushState({}, '', newUrl);
    }
    if (user && offerRedemptionMechanism == 'Dynamic') {
      setShowFullScreenQR(true);
    } else {
      if (isMobile) {
        setIsOfferRedemptionMobileViewOpen(!isOfferRedemptionMobileViewOpen);
      } else {
        setOpenRightDrawer(!openRightDrawer);
      }
    }
  };

  const handleOfferRedemptionMobileView = () => {
    setIsOfferRedemptionMobileViewOpen(!isOfferRedemptionMobileViewOpen);
  };

  const handleQRCodeScanned = (text: string) => {
    setEncryptedScannedText(text);
    setShowFullScreenQR(false);
    setIsQRCodeScanned(true);
    setOpenRightDrawer(true);
    if (isMobile) {
      setIsOfferRedemptionMobileViewOpen(true);
    }
  };

  const renderDateOrCountdown = () => {
    switch (countdownState.type) {
      case 'minutely': {
        const minuteText =
          countdownState.minutes === 1
            ? dictionary?.minuteLeft
            : dictionary?.minutesLeft;
        return (
          <div className="flex items-center text-bodys sm:text-bodyl font-bold text-text-default">
            <Flame className="h-6 w-6 lg:h-8 lg:w-8" viewBox="0 0 32 32" />
            {minuteText.replace('__', countdownState.minutes?.toString())}
          </div>
        );
      }
      case 'hourly': {
        const hourText =
          countdownState.hours === 1
            ? dictionary?.hourLeft
            : dictionary?.hoursLeft;
        return (
          <div className="flex items-center gap-1 text-bodys sm:text-bodyl font-bold text-text-default">
            <Flame className="h-6 w-6 lg:h-8 lg:w-8" viewBox="0 0 32 32" />
            {hourText.replace('__', countdownState.hours?.toString())}
          </div>
        );
      }
      default:
        return (
          <div className="text-bodys sm:text-bodyl text-text-default">
            {validFrom &&
              validTo &&
              dateRangeMedWithZoneOffset(
                validFrom.toString(),
                validTo.toString(),
                node_locale,
              )}
          </div>
        );
    }
  };
  const slotsSkeleton = () => {
    return (
      <div className="flex flex-column gap-6 mt-6">
        {Array.from({ length: _subSlotsSize }).map((_, index) => (
          <div key={index} className="animate-pulse flex flex-col space-y-4">
            <div className="h-40 w-full bg-background-secondary rounded-xl"></div>
          </div>
        ))}
      </div>
    );
  };

  const sendEventToDataLayer = () => {
    if (isBrowser && dataLayer) {
      dataLayer.push({
        event: 'booking-engine-opened',
        page_title: pageTitle,
        user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
      });
    }
  };

  const onBookingEngineButtonClick = () => {
    const bookingUrl = bookingEngineLink?.externalLink;
    const url = new URL(bookingUrl);
    const pathWithQuery = url.pathname + url.search;
    const prefixedPath = prefix ? `/${prefix}${pathWithQuery}` : pathWithQuery;
    if (user) {
      if (bookingUrl) {
        sendEventToDataLayer();
        redirect(prefixedPath + '?isHosted=true');
        // window.open(`${window.location.origin}${pathWithQuery}`, '_blank');
      }
    } else {
      openSlider(prefixedPath + '?isHosted=true');
    }
  };

  return (
    <>
      <BlockWrapper mode="box" className="pb-0 lg:mb-5 lg:pb-20">
        <PageComponentMainBottomBorder />
        {logo && (
          <div className="flex items-start mb-5 lg:mb-6">
            <div className="flex items-center justify-between">
              {logo && (
                <ImageWrapper
                  url={logo?.url}
                  alt={logo?.description ? logo.description : ''}
                  className="h-15 w-15 lg:h-20 lg:w-20 rounded-md bg-brand-white flex items-center border border-brand-grey-2"
                  imgClassName={
                    isNearlySquare
                      ? '!object-contain rounded-md'
                      : '!object-contain'
                  }
                  width={logo?.width ? parseInt(logo.width) : undefined}
                  height={logo?.height ? parseInt(logo.height) : undefined}
                />
              )}
              <p className="ml-2 text-headings lg:text-headingm">
                {tagItems && tagItems?.length > 0 && tagItems?.some((tag) => tag?.tagName === CAR_PARKING_TAG)
                  ? meetingPlaceHomePage?.name
                  : tenantName
                }
              </p >
            </div >
            {favouriteId && (
              <FavouriteButton
                id={favouriteId}
                type={favouriteType!}
                className="ml-auto h-12 w-12 md:h-16 md:w-16"
                iconclass="md:h-[32px] md:w-[34px]"
                name={header}
              />
            )}
          </div >
        )}
        {
          typeTag && (
            <div
              className={`font-title text-headings font-bold text-neutral-grey-700`}
            >
              {typeTag}
            </div>
          )
        }
        <div className={'flex justify-between'}>
          <PageComponentMainHeader
            header={header}
            caption={caption}
            className={!logo ? 'w-[85%]' : ''}
          />
          {!logo && favouriteType == 'event' && favouriteId && (
            <FavouriteButton
              id={favouriteId}
              type={favouriteType!}
              className="ml-auto h-12 w-12 md:h-16 md:w-16"
              iconclass="md:h-[32px] md:w-[34px]"
              name={header}
            />
          )}
        </div>

        {
          (!isLivliFamily || favouriteType != 'event') &&
          validTo &&
          validFrom && (
            <div className="text-headings text-neutral-grey-700 mb-6 lg:mb-8">
              {validFrom && validTo && renderDateOrCountdown()}
            </div>
          )
        }
        {
          isLivliFamily &&
          favouriteType == 'event' &&
          !showSlotsLoader &&
          eventSlotsData?.length > 0 &&
          activeEventSlotsData &&
          activeEventSlotsData?.length > 0 &&
          validTo &&
          validFrom &&
          (() => {
            const nextSlotText = getNextSlotText(
              activeEventSlotsData,
              node_locale,
              validFrom,
            );
            return (
              <div className="my-6 text-bodyl text-neutral-grey-700 ">
                <div className="font-sans font-bold flex items-center">
                  {nextSlotText?.variant == 'Today' ? (
                    <>
                      <Flame />
                      {dictionary?.today ?? 'Today'}
                    </>
                  ) : (
                    dictionary?.nextEvent + ':' + ' ' + nextSlotText?.date
                  )}
                </div>
                <div>
                  {dateRangeMedWithZoneOffset(validFrom, validTo, node_locale)}
                </div>
              </div>
            );
          })()
        }
        {
          showSpecialOfferRedemption &&
          typeOfOffer == 'specialOffer' &&
          validFrom &&
          validTo &&
          isDateTimeInRange(validFrom, validTo, timezone) &&
          !showClaimLoader && (
            <Button
              variant="primary"
              className="w-full md:w-[35%] lg:w-[30%] text-center text-neutral-grey-700 mb-8"
              onClick={claimOffer}
            >
              {dictionary?.claimOfferButtonText}
            </Button>
          )
        }
        {
          showSpecialOfferRedemption &&
          typeOfOffer == 'specialOffer' &&
          showClaimLoader && (
            <div className="w-full h-[44px] md:w-[35%] lg:w-[30%] mt-4 mb-8 rounded-full bg-accent-primary-normal lg:hover:bg-accent-primary-dark active:bg-brand-yellow-3 disabled:bg-neutral-grey-200 disabled:text-neutral-grey-300 animate-pulse"></div>
          )
        }

        {
          favouriteType == 'event' && tags && (
            <div className="mb-4 flex flex-wrap gap-2 mt-4">
              {tags
                .sort((a, b) => {
                  if (a === 'Livli Family') return -1;
                  if (b === 'Livli Family') return 1;
                  return 0;
                })
                ?.map((tag, index) => {
                  return (
                    <span
                      key={index}
                      className={`border-2 py-2 px-3 mr-2 rounded-md text-headingxxs text-neutral-grey-700 ${tag == 'Livli Family' ? 'border-brand-yellow-2' : 'border-neutral-grey-200'}`}
                    >
                      {tag}
                    </span>
                  );
                })}
            </div>
          )
        }
        {
          tagItems && tagItems?.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2 mt-2">
              {tagItems.map((tag) => {
                const borderColor = getTagBorderColor(tag.colour);
                return (
                  <span
                    key={tag.id}
                    className={`border-2 ${borderColor} py-2 px-3 rounded-md text-headingxxs text-neutral-grey-700 whitespace-nowrap`}
                  >
                    {tag.tagName}
                  </span>
                );
              })}
            </div>
          )}
        {tagItems && tagItems?.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2 mt-2">
            {tagItems.map((tag) => {
              const borderColor = getTagBorderColor(tag.colour);
              return (
                <span
                  key={tag.id}
                  className={`border-2 ${borderColor} py-2 px-3 rounded-md text-headingxxs text-neutral-grey-700 whitespace-nowrap`}
                >
                  {tag.tagName}
                </span>
              );
            })}
          </div>
        )}

        {(favouriteType == 'event' || favouriteType == 'offer') && richText && (
          <TenantDescription
            description={richText}
            node_locale={node_locale}
            onBookingClick={onBookingEngineButtonClick}
          />
        )}
        {favouriteType != 'event' && favouriteType != 'offer' && richText && (
          <RichText richText={richText} />
        )}
        {favouriteType == 'offer' && disclaimerText && (
          <p className="mt-6 text-bodyl text-neutral-grey-700 whitespace-pre-line font-bold">
            {`${dictionary?.disclaimerText}: ${disclaimerText}`}
          </p>
        )}
        {blocks && (
          <RenderBlocks
            blocks={blocks}
            node_locale={node_locale}
            meetingPlaceOpeningHours={meetingPlaceOpeningHours}
          />
        )}
        <div className="mt-8">
          {isLivliFamily &&
            favouriteType == 'event' &&
            !showSlotsLoader &&
            activeEventSlotsData &&
            activeEventSlotsData
              ?.slice(0, _subSlotsSize * slotsPage)
              .map((event: any) => (
                <SlotCard
                  key={event?.date}
                  event={{
                    ...event,
                    eventTitle: header,
                    timeSlots: event?.timeSlots.map((slot: any) => ({
                      ...slot,
                      href: bookingEngineLink?.externalLink,
                    })),
                  }}
                  onSlotClick={(slot, event) => {
                    const bookingUrl = bookingEngineLink?.externalLink;
                    const url = new URL(bookingUrl);
                    const pathWithQuery = url.pathname + url.search;
                    const prefixedPath = prefix
                      ? `/${prefix}${pathWithQuery}`
                      : pathWithQuery;
                    if (bookingUrl) {
                      if (user) {
                        sendEventToDataLayer();
                        redirect(prefixedPath + '?isHosted=true');
                      } else {
                        openSlider(prefixedPath + '?isHosted=true');
                      }
                    }
                  }}
                />
              ))}
          {isLivliFamily &&
            favouriteType == 'event' &&
            showSlotsLoader &&
            slotsSkeleton()}
        </div>
        <>
          {activeEventSlotsData?.length > _subSlotsSize - 1 && (
            <ShowMore
              currentPage={slotsPage}
              maxNum={activeEventSlotsData?.length}
              setPage={setSlotspage}
              pageSize={_subSlotsSize}
              ofString={dictionary?.of}
              showMoreString={dictionary?.showMore}
              showingString={dictionary?.showing}
            />
          )}
        </>
        {
          showSocialShare && (
            <Share className="absolute -bottom-2 right-0 lg:bottom-8 lg:right-20 xl:right-24" />
          )
        }
      </BlockWrapper >
      {isOfferRedemptionMobileViewOpen &&
        showSpecialOfferRedemption &&
        isMobile && (
          <>
            <Backdrop
              handleOpen={handleOfferRedemptionMobileView}
              showButtons={false}
              className="!min-h-[250px]"
            >
              <></>
              {isLivliFamilyOffer ? (
                <LivliFamilyOfferRedemption
                  key={encryptedScannedText}
                  node_locale={node_locale}
                  offerRedemptionDetails={offerRedemptionDetails}
                  offerRedemptionMechanism={offerRedemptionMechanism}
                  handleClose={() => {
                    setIsOfferRedemptionMobileViewOpen(
                      !isOfferRedemptionMobileViewOpen,
                    );
                    if (isBrowser) {
                      clearUrlParams();
                      localStorage.removeItem('current_path');
                      setIsQRCodeScanned(false);
                      setEncryptedScannedText('');
                    }
                  }}
                  isQRCodeScanned={isQRCodeScanned}
                  scannedText={encryptedScannedText}
                  contentful_id={contentful_id}
                  tenantDetails={{
                    offerName: header,
                    tenantName: tenantName,
                    tenantLogo: logo,
                  }}
                  meetingPlaceHomePage={meetingPlaceHomePage}
                  beResourceId={beResourceId}
                  offerTenantId={offerTenantId}
                />
              ) : (
                <SpecialOfferRedemtion
                  key={encryptedScannedText}
                  node_locale={node_locale}
                  offerRedemptionDetails={offerRedemptionDetails}
                  offerRedemptionMechanism={offerRedemptionMechanism}
                  handleClose={() => {
                    setIsOfferRedemptionMobileViewOpen(
                      !isOfferRedemptionMobileViewOpen,
                    );
                    if (isBrowser) {
                      clearUrlParams();
                      localStorage.removeItem('current_path');
                      setIsQRCodeScanned(false);
                      setEncryptedScannedText('');
                    }
                  }}
                  isQRCodeScanned={isQRCodeScanned}
                  scannedText={encryptedScannedText}
                  contentful_id={contentful_id}
                  tenantDetails={{
                    offerName: header,
                    tenantName: tenantName,
                    tenantLogo: logo,
                  }}
                  meetingPlaceHomePage={meetingPlaceHomePage}
                  offerBookingData={offerBookingData}
                />
              )}
              <button
                className="absolute -top-20 right-4 z-[200] grid h-12 w-12 place-items-center rounded-full bg-brand-grey-1 outline-0 transition-all focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:hidden lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark"
                onClick={() => {
                  setIsOfferRedemptionMobileViewOpen(false);
                  if (isBrowser) {
                    clearUrlParams();
                  }
                  localStorage.removeItem('current_path');
                  setIsQRCodeScanned(false);
                  setEncryptedScannedText('');
                }}
              >
                <CloseIcon />
              </button>
            </Backdrop>
          </>
        )}
      {!isMobile && showSpecialOfferRedemption && (
        <SideModal
          isOpen={openRightDrawer}
          handleClose={() => {
            setOpenRightDrawer(!openRightDrawer);
            if (isBrowser) {
              clearUrlParams();
              localStorage.removeItem('current_path');
              setIsQRCodeScanned(false);
            }
          }}
        >
          <SideModalBody bodyClass="h-full !my-0 !py-16">
            {isLivliFamilyOffer ? (
              <LivliFamilyOfferRedemption
                key={encryptedScannedText}
                node_locale={node_locale}
                offerRedemptionDetails={offerRedemptionDetails}
                offerRedemptionMechanism={offerRedemptionMechanism}
                handleClose={() => {
                  setOpenRightDrawer(!openRightDrawer);
                  if (isBrowser) {
                    clearUrlParams();
                    localStorage.removeItem('current_path');
                    setIsQRCodeScanned(false);
                    setEncryptedScannedText('');
                  }
                }}
                isQRCodeScanned={isQRCodeScanned}
                scannedText={encryptedScannedText}
                contentful_id={contentful_id}
                tenantDetails={{
                  offerName: header,
                  tenantName: tenantName,
                  tenantLogo: logo,
                }}
                meetingPlaceHomePage={meetingPlaceHomePage}
                beResourceId={beResourceId}
                offerTenantId={offerTenantId}
              />
            ) : (
              <SpecialOfferRedemtion
                key={encryptedScannedText}
                node_locale={node_locale}
                offerRedemptionDetails={offerRedemptionDetails}
                offerRedemptionMechanism={offerRedemptionMechanism}
                handleClose={() => {
                  setOpenRightDrawer(!openRightDrawer);
                  if (isBrowser) {
                    clearUrlParams();
                    localStorage.removeItem('current_path');
                    setIsQRCodeScanned(false);
                    setEncryptedScannedText('');
                  }
                }}
                isQRCodeScanned={isQRCodeScanned}
                scannedText={encryptedScannedText}
                contentful_id={contentful_id}
                tenantDetails={{
                  offerName: header,
                  tenantName: tenantName,
                  tenantLogo: logo,
                }}
                offerBookingData={offerBookingData}
                meetingPlaceHomePage={meetingPlaceHomePage}
              />
            )}
          </SideModalBody>
        </SideModal>
      )}
      {showFullScreenQR &&
        showSpecialOfferRedemption &&
        offerRedemptionMechanism == 'Dynamic' && (
          <ScanQRCode
            onScanned={handleQRCodeScanned}
            onClose={() => {
              setShowFullScreenQR(false);
              if (isBrowser) {
                clearUrlParams();
                localStorage.removeItem('current_path');
              }
            }}
            node_locale={node_locale}
          />
        )
      }
    </>
  );
};

